import {
  CONSTRUCTED_DECK_SLOT_LIMIT,
  content,
  isDeckPassiveId,
  validateConstructedDeck,
} from '@cards/cards';
import type { BuiltContent, DeckPassiveId, DeckValidationIssue } from '@cards/cards';
import type { CardDefId } from '@cards/engine';

export const SAVED_DECK_LIMIT = 20;
export { CONSTRUCTED_DECK_SLOT_LIMIT };

export interface SavedDeckDto {
  readonly id: string;
  readonly name: string;
  readonly cardIds: readonly CardDefId[];
  readonly passiveId: DeckPassiveId | null;
  readonly revision: number;
  readonly archivedAt: string | null;
  readonly createdAgainstContentVersion: number;
  readonly createdAgainstDeckRulesVersion: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly validation: Readonly<{
    readonly draftValid: boolean;
    readonly ready: boolean;
    readonly issues: readonly DeckValidationIssue[];
  }>;
}

export interface DeckLibrary {
  readonly decks: readonly SavedDeckDto[];
  readonly quota: Readonly<{
    readonly used: number;
    /** Ordinary manual create/copy quota. */
    readonly limit: number;
    /** Includes the single already-used onboarding reserve when present. */
    readonly effectiveLimit: number;
    readonly onboardingReserveUsed: boolean;
  }>;
  readonly rememberedDeckId: string | null;
  readonly availableCardIds: readonly CardDefId[];
}

export type DeckCreateSource = Readonly<{
  kind: 'draft';
  name: string;
  cardIds: readonly CardDefId[];
  passiveId: DeckPassiveId | null;
}>;

export interface DeckCreateResult {
  readonly deck: SavedDeckDto;
  readonly replayed: boolean;
}

export interface DeckPreferenceResult {
  readonly rememberedDeckId: string;
}

export interface CreateDeckOptions {
  /** UUID retained by the caller while retrying a request after a lost ACK. */
  readonly operationId?: string;
  readonly signal?: AbortSignal;
}

export interface DeckUpdateInput {
  readonly name: string;
  readonly cardIds: readonly CardDefId[];
  readonly passiveId: DeckPassiveId | null;
  readonly expectedRevision: number;
}

export interface DeckApi {
  list(includeArchived: boolean, signal?: AbortSignal): Promise<DeckLibrary>;
  get(deckId: string, signal?: AbortSignal): Promise<SavedDeckDto>;
  create(
    source: DeckCreateSource,
    csrfToken: string,
    options?: CreateDeckOptions,
  ): Promise<DeckCreateResult>;
  update(
    deckId: string,
    input: DeckUpdateInput,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<SavedDeckDto>;
  setArchived(
    deckId: string,
    archived: boolean,
    expectedRevision: number,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<SavedDeckDto>;
  delete(
    deckId: string,
    expectedRevision: number,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<void>;
  setPreference(
    deckId: string,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<DeckPreferenceResult>;
}

export type DeckApiErrorCode =
  | 'ACCOUNT_REQUIRED'
  | 'PROFILE_INCOMPLETE'
  | 'FORBIDDEN_ORIGIN'
  | 'DECK_NOT_FOUND'
  | 'DECK_REVISION_CONFLICT'
  | 'DECK_LIMIT_REACHED'
  | 'OPERATION_ID_CONFLICT'
  | 'DECK_INVALID'
  | 'DECK_NAME_INVALID'
  | 'INVALID_REQUEST'
  | 'REQUEST_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'SERVICE_UNAVAILABLE'
  | 'INTERNAL_ERROR'
  | 'METHOD_NOT_ALLOWED'
  | 'NOT_FOUND'
  | 'INVALID_RESPONSE';

export class DeckApiError extends Error {
  override readonly name = 'DeckApiError';

  constructor(
    readonly code: DeckApiErrorCode,
    readonly status: number,
    readonly currentRevision?: number,
  ) {
    super(code);
  }
}

type JsonObject = Record<string, unknown>;

function strictObject(value: unknown, keys: readonly string[], label: string): JsonObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label}: expected object`);
  }
  const object = value as JsonObject;
  const actual = Object.keys(object);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label}: unknown or missing fields`);
  }
  return object;
}

function string(value: unknown, label: string, maximum = 256): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > maximum) {
    throw new Error(`${label}: expected bounded string`);
  }
  return value;
}

const FORBIDDEN_NAME_PATTERN = /[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/u;
const UNPAIRED_SURROGATE_PATTERN = /[\uD800-\uDFFF]/u;

function deckName(value: unknown, label: string): string {
  if (typeof value !== 'string'
    || value !== value.normalize('NFKC').trim()
    || [...value].length < 1
    || [...value].length > 60
    || FORBIDDEN_NAME_PATTERN.test(value)
    || UNPAIRED_SURROGATE_PATTERN.test(value)) {
    throw new Error(`${label}: expected normalized deck name`);
  }
  return value;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

export function parseDeckUuid(value: unknown, label = 'deck id'): string {
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
    throw new Error(`${label}: expected lowercase UUID`);
  }
  return value;
}

function positiveSafeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new Error(`${label}: expected positive safe integer`);
  }
  return value as number;
}

function nonNegativeSafeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new Error(`${label}: expected non-negative safe integer`);
  }
  return value as number;
}

function boolean(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`${label}: expected boolean`);
  return value;
}

function timestamp(value: unknown, label: string): string {
  const result = string(value, label, 64);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(result)
    || !Number.isFinite(Date.parse(result))
    || new Date(result).toISOString() !== result) {
    throw new Error(`${label}: expected UTC timestamp`);
  }
  return result;
}

function parseCardIds(value: unknown, label: string): readonly CardDefId[] {
  if (!Array.isArray(value) || value.length > CONSTRUCTED_DECK_SLOT_LIMIT) {
    throw new Error(`${label}: expected at most 12 cards`);
  }
  return Object.freeze(value.map((item, index) => string(item, `${label}[${index}]`) as CardDefId));
}

function parsePassiveId(value: unknown, label = 'passiveId'): DeckPassiveId | null {
  if (value === null) return null;
  if (!isDeckPassiveId(value)) throw new Error(`${label}: expected stable lowercase id`);
  return value;
}

function parseSlotIndexes(value: unknown): readonly number[] {
  if (!Array.isArray(value)) throw new Error('slotIndexes: expected array');
  return Object.freeze(value.map((item) => {
    const index = nonNegativeSafeInteger(item, 'slot index');
    if (index >= CONSTRUCTED_DECK_SLOT_LIMIT) throw new Error('slot index: out of range');
    return index;
  }));
}

function parseValidationIssue(value: unknown): DeckValidationIssue {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('validation issue: expected object');
  }
  const code = (value as JsonObject).code;
  if (code === 'DECK_TOO_MANY_SLOTS') {
    const object = strictObject(value, ['code', 'actualSlots', 'maximumSlots'], 'too many slots');
    const maximumSlots = positiveSafeInteger(object.maximumSlots, 'maximumSlots');
    if (maximumSlots !== CONSTRUCTED_DECK_SLOT_LIMIT) throw new Error('maximumSlots mismatch');
    return Object.freeze({
      code,
      actualSlots: nonNegativeSafeInteger(object.actualSlots, 'actualSlots'),
      maximumSlots: CONSTRUCTED_DECK_SLOT_LIMIT,
    });
  }
  if (code === 'DECK_NOT_READY') {
    const object = strictObject(value, ['code', 'actualSlots', 'requiredSlots'], 'not ready');
    const requiredSlots = positiveSafeInteger(object.requiredSlots, 'requiredSlots');
    if (requiredSlots !== CONSTRUCTED_DECK_SLOT_LIMIT) throw new Error('requiredSlots mismatch');
    return Object.freeze({
      code,
      actualSlots: nonNegativeSafeInteger(object.actualSlots, 'actualSlots'),
      requiredSlots: CONSTRUCTED_DECK_SLOT_LIMIT,
    });
  }
  if (code === 'DECK_UNKNOWN_CARD') {
    const object = strictObject(value, ['code', 'cardId', 'slotIndexes'], 'unknown card');
    return Object.freeze({
      code,
      cardId: string(object.cardId, 'cardId') as CardDefId,
      slotIndexes: parseSlotIndexes(object.slotIndexes),
    });
  }
  if (code === 'DECK_CARD_NOT_PLAYABLE') {
    const object = strictObject(value, ['code', 'cardId', 'role', 'slotIndexes'], 'unplayable card');
    const role = object.role;
    if (role !== 'auxiliary' && role !== 'standalone_lab' && role !== 'legacy') {
      throw new Error('role: unknown card role');
    }
    return Object.freeze({
      code,
      cardId: string(object.cardId, 'cardId') as CardDefId,
      role,
      slotIndexes: parseSlotIndexes(object.slotIndexes),
    });
  }
  if (code === 'DECK_UNKNOWN_PASSIVE') {
    const object = strictObject(value, ['code', 'passiveId'], 'unknown passive');
    const passiveId = parsePassiveId(object.passiveId, 'passiveId');
    if (passiveId === null) throw new Error('passiveId: expected non-null id');
    return Object.freeze({ code, passiveId });
  }
  if (code === 'DECK_PASSIVE_NOT_AVAILABLE') {
    const object = strictObject(value, ['code', 'passiveId', 'role'], 'unavailable passive');
    const passiveId = parsePassiveId(object.passiveId, 'passiveId');
    if (passiveId === null || object.role !== 'legacy') {
      throw new Error('unavailable passive: invalid id or role');
    }
    return Object.freeze({ code, passiveId, role: 'legacy' });
  }
  throw new Error('validation issue: unknown code');
}

export function parseSavedDeckDto(
  value: unknown,
  source: Pick<BuiltContent, 'cards' | 'deckPassives'> = content,
): SavedDeckDto {
  const object = strictObject(value, [
    'id', 'name', 'cardIds', 'passiveId', 'revision', 'archivedAt',
    'createdAgainstContentVersion', 'createdAgainstDeckRulesVersion',
    'createdAt', 'updatedAt', 'validation',
  ], 'saved deck');
  const validation = strictObject(
    object.validation,
    ['draftValid', 'ready', 'issues'],
    'deck validation',
  );
  if (!Array.isArray(validation.issues)) throw new Error('validation issues: expected array');
  const archivedAt = object.archivedAt === null
    ? null
    : timestamp(object.archivedAt, 'archivedAt');
  const cardIds = parseCardIds(object.cardIds, 'cardIds');
  const passiveId = parsePassiveId(object.passiveId);
  const issues = Object.freeze(validation.issues.map(parseValidationIssue));
  const authoritativeValidation = validateConstructedDeck({ cardIds, passiveId }, source);
  if (boolean(validation.draftValid, 'draftValid') !== authoritativeValidation.draftValid
    || boolean(validation.ready, 'ready') !== authoritativeValidation.ready
    || JSON.stringify(issues) !== JSON.stringify(authoritativeValidation.issues)) {
    throw new Error('deck validation: inconsistent with card slots');
  }
  return Object.freeze({
    id: parseDeckUuid(object.id),
    name: deckName(object.name, 'name'),
    cardIds,
    passiveId,
    revision: positiveSafeInteger(object.revision, 'revision'),
    archivedAt,
    createdAgainstContentVersion: positiveSafeInteger(
      object.createdAgainstContentVersion,
      'createdAgainstContentVersion',
    ),
    createdAgainstDeckRulesVersion: positiveSafeInteger(
      object.createdAgainstDeckRulesVersion,
      'createdAgainstDeckRulesVersion',
    ),
    createdAt: timestamp(object.createdAt, 'createdAt'),
    updatedAt: timestamp(object.updatedAt, 'updatedAt'),
    validation: Object.freeze({
      draftValid: authoritativeValidation.draftValid,
      ready: authoritativeValidation.ready,
      issues,
    }),
  });
}

export function parseDeckLibrary(value: unknown): DeckLibrary {
  const object = strictObject(
    value,
    ['decks', 'quota', 'rememberedDeckId', 'availableCardIds'],
    'deck library',
  );
  if (!Array.isArray(object.decks)) throw new Error('decks: expected array');
  const quota = strictObject(
    object.quota,
    ['used', 'limit', 'effectiveLimit', 'onboardingReserveUsed'],
    'quota',
  );
  const used = nonNegativeSafeInteger(quota.used, 'quota.used');
  const limit = positiveSafeInteger(quota.limit, 'quota.limit');
  const effectiveLimit = positiveSafeInteger(quota.effectiveLimit, 'quota.effectiveLimit');
  const onboardingReserveUsed = boolean(
    quota.onboardingReserveUsed,
    'quota.onboardingReserveUsed',
  );
  if (limit !== SAVED_DECK_LIMIT
    || effectiveLimit !== limit + (onboardingReserveUsed ? 1 : 0)
    || used > effectiveLimit) {
    throw new Error('quota: invalid bounds');
  }
  const decks = Object.freeze(object.decks.map((deck) => parseSavedDeckDto(deck)));
  if (new Set(decks.map((deck) => deck.id)).size !== decks.length) {
    throw new Error('decks: duplicate id');
  }
  const rememberedDeckId = object.rememberedDeckId === null
    ? null
    : parseDeckUuid(object.rememberedDeckId, 'rememberedDeckId');
  if (decks.length > used
    || (rememberedDeckId !== null && !decks.some((deck) => deck.id === rememberedDeckId))) {
    throw new Error('deck library: inconsistent quota or remembered deck');
  }
  if (!Array.isArray(object.availableCardIds)) {
    throw new Error('availableCardIds: expected array');
  }
  const constructible = new Set(
    [...content.cards.values()].filter((card) => card.role === 'playable').map((card) => card.id),
  );
  const availableCardIds = Object.freeze(object.availableCardIds.map((value, index) => {
    const cardId = string(value, `availableCardIds[${index}]`) as CardDefId;
    if (!constructible.has(cardId)) throw new Error('availableCardIds: non-constructible card');
    return cardId;
  }));
  if (new Set(availableCardIds).size !== availableCardIds.length) {
    throw new Error('availableCardIds: duplicate card');
  }
  return Object.freeze({
    decks,
    quota: Object.freeze({ used, limit, effectiveLimit, onboardingReserveUsed }),
    rememberedDeckId,
    availableCardIds,
  });
}

export function parseDeckCreateResult(value: unknown): DeckCreateResult {
  const object = strictObject(value, ['deck', 'replayed'], 'deck create result');
  return Object.freeze({
    deck: parseSavedDeckDto(object.deck),
    replayed: boolean(object.replayed, 'replayed'),
  });
}

export function parseDeckPreferenceResult(value: unknown): DeckPreferenceResult {
  const object = strictObject(value, ['rememberedDeckId'], 'deck preference result');
  return Object.freeze({ rememberedDeckId: parseDeckUuid(object.rememberedDeckId, 'rememberedDeckId') });
}
